import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import AppImage from '../../components/AppImage';
import { Phone, Users, Luggage } from 'lucide-react';
import { localCabPricing } from '../../data/cityPricingData';

const LocalCabCity = () => {
  const { cityName } = useParams();
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState('4hrs-40km');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);

  const formattedCityName = cityName?.split('-')?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');

  const durations = [
  { id: '4hrs-40km', label: '4 hrs | 40KM' },
  { id: '8hrs-80km', label: '8 hrs | 80KM' },
  { id: '12hrs-120km', label: '12 hrs | 120KM' }];


  // Get city-specific pricing from centralized data
  const vehicles = localCabPricing?.[cityName?.toLowerCase()] || [];


  const handleSelectCar = (vehicle) => {
    navigate('/car-details', {
      state: {
        vehicle,
        route: {
          from: 'Local',
          to: cityName
        }
      }
    });
  };

  return (
    <>
      <SEO
        title={`Local Cab In ${formattedCityName} - Hourly Cab Booking | Advait Tour and Travels`}
        description={`Book local cab in ${formattedCityName} for hourly trips. Choose from 4hrs, 8hrs, or 12hrs packages. Reliable service with professional drivers.`}
        keywords={`local cab ${cityName}, ${cityName} taxi service, hourly cab booking ${cityName}`}
        canonicalUrl={`${window.location?.origin}/local-cab/${cityName}`}
        ogTitle={`Local Cab In ${formattedCityName} - Hourly Cab Booking | Advait Tour and Travels`}
        ogDescription={`Book local cab in ${formattedCityName} for hourly trips. Choose from 4hrs, 8hrs, or 12hrs packages. Reliable service with professional drivers.`}
        ogUrl={`${window.location?.origin}/local-cab/${cityName}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TaxiService",
          "name": "Advait Tour and Travels",
          "description": `Local cab service in ${formattedCityName}`
        }} />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-16 mb-12">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-6">
                Local Cab In {formattedCityName}
              </h1>
              
              {/* Duration Tabs */}
              <div className="flex flex-wrap justify-center gap-4">
                {durations?.map((duration) =>
                <button
                  key={duration?.id}
                  onClick={() => setSelectedDuration(duration?.id)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                  selectedDuration === duration?.id ?
                  'bg-yellow-400 text-gray-900' : 'bg-white/20 text-white hover:bg-white/30'}`
                  }>

                    {duration?.label}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle Cards */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 gap-6 max-w-6xl mx-auto">
              {vehicles?.map((vehicle, index) =>
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">

                  <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                    {/* Vehicle Image */}
                    <div className="w-full md:w-64 h-48 flex-shrink-0">
                      <AppImage
                      src={vehicle?.image}
                      alt={vehicle?.alt}
                      className="w-full h-full object-contain" />

                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 flex flex-col md:flex-row items-center justify-between gap-6 w-full">
                      {/* Car Type and Name */}
                      <div className="text-center md:text-left">
                        <p className="text-sm text-gray-500 font-medium uppercase mb-1">CAR TYPE</p>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{vehicle?.type}</h3>
                        <button className="text-green-600 font-semibold text-sm hover:underline">
                          More Details
                        </button>
                      </div>

                      {/* Specifications */}
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Luggage className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.luggage} Luggage</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Users className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.seats} Seats</p>
                          </div>
                        </div>
                      </div>

                      {/* Price and Button */}
                      <div className="flex flex-col items-center gap-3">
                        <div className="text-right">
                          <p className="text-3xl font-bold text-green-600">₹{vehicle?.price?.toLocaleString('en-IN')}/-</p>
                          <p className="text-sm text-gray-500">Inc. of Taxes*</p>
                        </div>
                        <button
                        onClick={() => handleSelectCar(vehicle)}
                        className="px-8 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all duration-200 whitespace-nowrap">

                          Select Car
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Visitor's Guide */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Visitor's Guide
              </h2>
              
              <div className="prose max-w-none space-y-6 text-gray-700">
                <h3 className="text-2xl font-bold text-gray-800">
                  Best Local Cab Service in {formattedCityName} – Reliable Convenient Rides
                </h3>
                <p>
                  Looking for the best taxi service in {formattedCityName}? Whether you need a one-way trip, a round trip, or a local trip around the city, Advait Tour and Travels is your go-to solution. We offer reliable, comfortable, and affordable transportation to make your travel hassle-free. From business meetings to casual outings, our local cab in {formattedCityName} ensures timely pickups and drops at competitive rates.
                </p>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Why Choose Advait Tour and Travels for Your Local Cab in {formattedCityName}?
                </h3>
                <p>
                  At Advait Tour and Travels, we prioritize customer satisfaction. With years of experience in the industry, we offer professional drivers, well-maintained vehicles, and 24/7 availability. Here's why we are the best taxi service in {formattedCityName}:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Convenient Booking:</strong> Whether you want to book a cab in {formattedCityName} for a local trip or plan a longer journey, our booking process is easy. Just a few clicks, and your ride will be on its way!</li>
                  <li><strong>Comfort & Safety:</strong> Our fleet consists of modern, air-conditioned vehicles that provide maximum comfort and safety. All drivers are trained professionals who ensure a smooth ride.</li>
                  <li><strong>Flexible Options:</strong> Whether it's a one-way trip or a round trip, we have flexible cab packages to suit your requirements.</li>
                  <li><strong>Affordable Rates:</strong> Our prices are transparent with no hidden charges, ensuring you get the best taxi service in {formattedCityName} at affordable rates.</li>
                </ol>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Types of Taxi Services We Offer in {formattedCityName}
                </h3>
                <h4 className="text-xl font-bold text-gray-800 mt-4">1. One-Way Trip</h4>
                <p>
                  Need a ride to a specific destination? Book a cab in {formattedCityName} for a one-way trip to get you where you need to be, without any fuss. Whether it's an airport drop or a city transfer, we offer convenient one-way taxi services at the best prices.
                </p>

                <h4 className="text-xl font-bold text-gray-800 mt-4">2. Round Trip</h4>
                <p>
                  Planning a journey where you'll need to return the same day? Our round trip service is perfect for travelers who want to complete their journey with ease. You'll have the same cab waiting to bring you back, ensuring a stress-free return trip.
                </p>

                <h4 className="text-xl font-bold text-gray-800 mt-4">3. Local Trip</h4>
                <p>
                  Exploring {formattedCityName} or need to get around for errands? Our local trip service provides hourly cab options, letting you book a local cab in {formattedCityName} to take you to multiple locations around town. Whether it's shopping, visiting tourist attractions, or business meetings, we've got you covered.
                </p>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Popular Destinations We Serve in {formattedCityName}
                </h3>
                <p>
                  {formattedCityName} is a growing town with several key locations, and Advait Tour and Travels is here to provide seamless transportation. Here are some of the popular routes for which customers frequently book our taxi service in {formattedCityName}:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Local Sightseeing:</strong> Whether you're a tourist or a local, our local trip service is perfect for exploring {formattedCityName}.</li>
                  <li><strong>Airport Transfers:</strong> We provide hassle-free taxi services for one-way trips to and from nearby airports. Our airport transfer service ensures you're always on time for your flight.</li>
                  <li><strong>Business and Corporate Rides:</strong> Need to attend a meeting in a nearby city or industrial hub? Book a cab in {formattedCityName} with our round trip service, ensuring that your business travel is efficient and comfortable.</li>
                  <li><strong>Outstation Trips:</strong> Planning an outstation journey? We provide cabs for destinations beyond {formattedCityName}, ensuring a smooth and enjoyable ride to your desired location.</li>
                </ol>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  How to Book a Cab in {formattedCityName}?
                </h3>
                <p>Booking your ride with Advait Tour and Travels is simple and quick. Follow these steps to get the best taxi service in {formattedCityName}:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Call or Book Online:</strong> You can easily book through our website or give us a call to arrange your trip.</li>
                  <li><strong>Choose Your Trip Type:</strong> Select from one-way trip, round trip, or local trip services based on your needs.</li>
                  <li><strong>Confirm Your Ride:</strong> Once you've provided the necessary details like pick-up location, drop-off point, and time, we'll confirm your booking and dispatch the nearest cab.</li>
                  <li><strong>Enjoy Your Journey:</strong> Sit back and relax while our experienced driver takes you to your destination safely and on time.</li>
                </ol>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Our Fleet of Vehicles
                </h3>
                <p>
                  At Advait Tour and Travels, we provide a range of vehicles to suit every traveler's needs. Whether you're traveling solo or with a group, we have the perfect cab for you. Our fleet includes:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Sedans:</strong> Ideal for individual or small group travel, offering comfort and style for short trips.</li>
                  <li><strong>SUVs:</strong> Perfect for families or larger groups, offering extra space and luxury.</li>
                  <li><strong>Hatchbacks:</strong> A budget-friendly option for local and short-distance travel.</li>
                </ol>
                <p>No matter which vehicle you choose, you can count on us for a safe and pleasant journey.</p>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Customer-Centric Services
                </h3>
                <p>At Advait Tour and Travels, we believe in providing top-notch customer service. Here's how we ensure an excellent experience:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>24/7 Availability:</strong> No matter when you need a ride, we're available around the clock to assist you.</li>
                  <li><strong>Experienced Drivers:</strong> Our professional drivers are familiar with {formattedCityName} and nearby areas, ensuring timely and smooth rides.</li>
                  <li><strong>Real-Time Tracking:</strong> You can track your ride in real time using our app, ensuring you're always informed about the cab's arrival.</li>
                </ol>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Why Advait Tour and Travels is the Best Taxi Service in {formattedCityName}
                </h3>
                <ol className="list-decimal pl-6 space-y-2">
                  <li><strong>Reliable Service:</strong> Whether it's a last-minute booking or a scheduled ride, we're always punctual and dependable.</li>
                  <li><strong>Affordability:</strong> We offer competitive rates with no hidden charges, making us the go-to taxi service for budget-conscious travelers.</li>
                  <li><strong>Customizable Packages:</strong> Whether you need a local trip for a few hours or a full-day outstation cab, we offer packages tailored to your needs.</li>
                </ol>

                <p className="mt-8">
                  Ready to experience the best taxi service in {formattedCityName}? Don't wait – book a cab in {formattedCityName} with Advait Tour and Travels and enjoy a comfortable, affordable ride for all your travel needs. Whether it's a one-way trip, round trip, or local trip, we are here to make your journey pleasant and stress-free.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300">

                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default LocalCabCity;